import React, { useState } from 'react';
import { useEffect, useRef } from 'react';
import './GeoLocationInput.css';

declare global {
  interface Window {
    google: any;
  }
}


const GeoLocationInput = ({ width, height, placeholder, onLocationSelect, displayError, onErrorChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.google) {
      initAutocomplete();
    } else {
      const checkGoogleMapsLoaded = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogleMapsLoaded);
          initAutocomplete();
        }
      }, 100);
      return () => clearInterval(checkGoogleMapsLoaded);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(inputRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  const initAutocomplete = () => {
    if (!inputRef.current) return;
    const options = {
      types: ['(regions)'], // This restricts to cities, states, and zip codes
      componentRestrictions: { country: 'us' }, // Optional: restrict to US
    };

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    document.addEventListener('mousedown', (e) => {
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer && pacContainer.contains(e.target as Node)) {
        console.log('clicked on suggestion');
        setIsSelecting(true);
      }
    });

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
  };

  const handlePlaceSelect = () => {
    setIsLoading(true);
    onErrorChange('');
    setHasSelectedPlace(true);
    try {
      const place = autocompleteRef.current.getPlace();
      
      if (!place.geometry) {
        onErrorChange('No location data available for this input');
        setIsLoading(false);
        return;
      }

      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: place.formatted_address,
        placeId: place.place_id,
        name: place.name
      };

      onLocationSelect(location);
      setInputValue(location.address);
    } catch (err) {
      onErrorChange('Error retrieving location data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setHasSelectedPlace(false);
    onErrorChange('');
  };

  const handleInputBlur = () => {
    if (inputValue && !hasSelectedPlace && !isSelecting) {
      onErrorChange('Please select an option after input your location.');
    } else {
      setIsSelecting(false);
    }
  };


  return (
    <div className="geo-location-input">
      <div className={displayError ? "input-wrapper error-wrap" : "input-wrapper"} style={{ width: viewportWidth > 900 ? width : '', height: height }}>
        <img src="/images/location-icon2.svg" alt="Search" className="search-icon" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="location-input"
        />
    
      </div>
        {displayError && <div className="error-message">{displayError}</div>}
    </div>
  );
};

export default GeoLocationInput;